import { IconCloseCircle } from '@blockchain-com/icons'
import Icon from 'components/Icons/Icon'
import { fastFade } from 'components/Transition'
import * as React from 'react'
import styled, { css } from 'styled-components'
import { color, screenWidth, zIndex } from 'utils'

type ModalProps = {
  background?: string
  fullscreen: boolean
  largescreen: boolean
}

type PropsType = ModalProps & {
  children: React.ReactNode
  isDismissable: boolean
  onClickOut: React.MouseEventHandler<HTMLDivElement>
  onClose: () => void
}

const largescreenCss = css`
  max-width: 90%;
`

const fullscreenCss = css`
  height: 100%;
  width: 100%;
`

const ModalBackground = styled.div<{ fullscreen: boolean }>`
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  position: fixed;
  display: flex;
  padding: 0 ${({ fullscreen }) => (fullscreen ? '0' : '1rem')};
  justify-content: center;
  overflow-y: ${({ fullscreen }) => (fullscreen ? 'hidden' : 'auto')};
  background: ${color('blackAlpha75')};
  z-index: ${zIndex(4)};
  ${fastFade};
`

const InnerModal = styled.div`
  display: flex;
  align-items: center;
  flex-flow: wrap;
  flex: 1 0 100%;
`

const Modal = styled.div<ModalProps>`
  position: relative;
  display: flex;
  max-width: ${({ fullscreen, largescreen }) => (fullscreen || largescreen ? '100%' : '540px')};
  align-items: stretch;
  justify-content: stretch;
  margin: ${({ fullscreen }) => (fullscreen ? '0' : '1rem')} auto;
  box-shadow: 0 0 3rem 2px transparent;
  background: ${(p) => (p.background ? color(p.background) : color('white'))};
  border-radius: ${({ fullscreen }) => (fullscreen ? '0' : '0.5rem')};
  overflow-y: auto;
  ${({ fullscreen, largescreen }) =>
    fullscreen ? fullscreenCss : largescreen ? largescreenCss : ''};

  @media only screen and (max-width: ${screenWidth('sm')}) {
    width: 100vw;
  }
`

const IconWrapper = styled.div`
  svg {
    position: absolute;
    cursor: pointer;
    top: ${screenWidth('xs') ? '1.5rem' : '1rem'};
    right: ${screenWidth('xs') ? '2rem' : '1rem'};
    z-index: ${zIndex(1)};
  }
`

const BaseModal = ({
  background,
  children,
  fullscreen,
  isDismissable,
  largescreen,
  onClickOut,
  onClose
}: PropsType) => {
  const ref = React.useRef<HTMLDivElement>(null)

  return (
    <ModalBackground
      fullscreen={fullscreen}
      onMouseDown={(e) => {
        const isOutOfModalContent = !ref.current?.contains(e.target as Node)
        if (isOutOfModalContent) {
          onClickOut(e)
        }
      }}
    >
      <InnerModal>
        <Modal background={background} fullscreen={fullscreen} largescreen={largescreen} ref={ref}>
          {isDismissable && (
            <IconWrapper onClick={onClose}>
              <Icon color='grey400' size='sm'>
                <IconCloseCircle />
              </Icon>
            </IconWrapper>
          )}
          {children}
        </Modal>
      </InnerModal>
    </ModalBackground>
  )
}

export default BaseModal
